import React from 'react';
import { Button, Table, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { istDateTime } from '@/utils/cricketCalendar';
import type { PvpSyncWorstMatch } from '@/types/pvp';
import { copyText, fmtNum, phoneLabel } from './model';

const { Text } = Typography;

/** Copies the full match id; no hooks, so a test can call it and press the button. */
export const CopyMatchId = ({ id }: { id: string }) => (
  <Button
    type="text"
    size="small"
    icon={<CopyOutlined />}
    aria-label={`Copy match id ${id}`}
    onClick={async () => {
      if (await copyText(id)) message.success('Match id copied');
      else message.error('Could not copy the match id');
    }}
  />
);

const columns: ColumnsType<PvpSyncWorstMatch> = [
  {
    title: 'Match',
    key: 'matchId',
    render: (_, w) => (
      <span>
        <Text code title={w.matchId}>
          {w.matchId.slice(0, 8)}
        </Text>
        <CopyMatchId id={w.matchId} />
      </span>
    ),
  },
  { title: 'Ended', key: 'endedAt', render: (_, w) => istDateTime(w.endedAt) },
  { title: 'Phone A', key: 'a', render: (_, w) => phoneLabel(w.appA, w.bvA, w.platformA) },
  { title: 'Phone B', key: 'b', render: (_, w) => phoneLabel(w.appB, w.bvB, w.platformB) },
  { title: 'Balls', key: 'balls', align: 'right', render: (_, w) => fmtNum(w.balls, 0) },
  {
    title: 'Mismatches',
    key: 'mismatches',
    align: 'right',
    render: (_, w) => fmtNum(w.mismatches, 0),
  },
  {
    title: 'Start error p95 (ms)',
    key: 'startErrP95',
    align: 'right',
    render: (_, w) => fmtNum(w.startErrP95),
  },
  {
    title: 'Check p95 (ms)',
    key: 'checkP95',
    align: 'right',
    render: (_, w) => fmtNum(w.checkP95),
  },
];

/** The matches with the most mismatches, then the largest start error. */
export const WorstTable: React.FC<{ worst: PvpSyncWorstMatch[] }> = ({ worst }) => (
  <Table<PvpSyncWorstMatch>
    size="small"
    pagination={false}
    rowKey="matchId"
    columns={columns}
    dataSource={worst}
    scroll={{ x: true }}
  />
);
